import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, retry } from 'rxjs';
import { environment } from '../../environments/environment';

/** Settlement status derived by the API. Mirrors PaymentStatuses in the Domain layer. */
export type PaymentStatus =
  | 'Not Applicable'
  | 'Paid On Time'
  | 'Paid Late'
  /** Marked Paid with no Paid Date recorded — settled, but timeliness unknown. */
  | 'Paid'
  | 'Due'
  | 'Overdue';

export interface PaymentTrackingItem {
  id: string;
  projectId: string;
  number: string;
  paymentMilestone: string;
  percentage: number;
  targetDate?: string;
  invoicedDate?: string;
  paymentDueDate?: string;
  /** Per-milestone override of the project's payment term, in calendar days. */
  paymentTermDays?: number;
  paidDate?: string;
  invoicingStatus: string;
  comments?: string;

  // Derived server-side from the current date; read-only, never sent back as input.
  paymentStatus?: PaymentStatus;
  overdueDays?: number;
  effectivePaymentTermDays?: number;
}

export interface CreatePaymentTrackingItemDto {
  projectId: string;
  number: string;
  paymentMilestone: string;
  percentage: number;
  targetDate?: string;
  invoicedDate?: string;
  paymentDueDate?: string;
  paymentTermDays?: number;
  paidDate?: string;
  invoicingStatus: string;
  comments?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentTrackingItemService {
  private apiUrl = `${environment.apiUrl}/PaymentTrackingItem`;

  constructor(private http: HttpClient) { }

  getByProjectId(projectId: string): Observable<PaymentTrackingItem[]> {
    return this.http.get<PaymentTrackingItem[]>(`${this.apiUrl}/project/${projectId}`);
  }

  getById(id: string): Observable<PaymentTrackingItem> {
    return this.http.get<PaymentTrackingItem>(`${this.apiUrl}/${id}`);
  }

  addItem(item: CreatePaymentTrackingItemDto): Observable<PaymentTrackingItem> {
    return this.http.post<PaymentTrackingItem>(this.apiUrl, item);
  }

  updateItem(id: string, item: PaymentTrackingItem): Observable<PaymentTrackingItem> {
    return this.http.put<PaymentTrackingItem>(`${this.apiUrl}/${id}`, item);
  }

  deleteItem(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  getTotalInvoicedPercentage(projectId: string): Observable<{ totalInvoicedPercentage: number }> {
    return this.http.get<{ totalInvoicedPercentage: number }>(`${this.apiUrl}/project/${projectId}/total-invoiced-percentage`);
  }
}


